import { useMemo, memo } from "react";
import { useBreakpoints } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";

import MobileCardSelector from "./MobileCardSelector";
import DesktopCardSelector from "./DesktopCardSelector";

export interface CardSelectorOption {
  id: API.CardId;
  label: string;
}

export interface CardSelectorProps {
  cardId?: API.CardId;
  cards: API.Card[];
  onChange: (id: API.CardId) => void;
}

const MAX_CARD_NAME_LENGTH = 25;

const CardSelector = ({ cardId, cards, onChange }: CardSelectorProps) => {
  const { m } = useBreakpoints();
  const { l10n } = useLocalization();

  if (!cardId) {
    return null;
  }

  const options: CardSelectorOption[] = useMemo(
    () =>
      cards.map((card) => {
        const cardName =
          card.name.length > MAX_CARD_NAME_LENGTH
            ? `${card.name.slice(0, MAX_CARD_NAME_LENGTH)}...`
            : card.name;
        const defaultLabel = `${cardName} - ${card.last_four_digits}`;
        const label = l10n.getString(
          "subscription-card-option",
          { cardName, last4: card.last_four_digits },
          defaultLabel
        );
        return { id: card.id, label };
      }),
    [cards, l10n]
  );

  if (!m) {
    return (
      <MobileCardSelector
        cardId={cardId}
        options={options}
        onChange={onChange}
      />
    );
  }

  return (
    <DesktopCardSelector
      cardId={cardId}
      options={options}
      onChange={onChange}
    />
  );
};

export default memo(CardSelector);
